import Box from '@mui/material/Box';
import ButtonLink from 'common/components/Elements/ButtonLink/ButtonLink';
import ButtonLoading from 'common/components/Elements/ButtonLoading/ButtonLoading';
import useFileDownload from 'common/utilities/hooks/useFileDownload/useFileDownload';
import useShortcut from 'common/utilities/hooks/useShortcut/useShortcut';
import classes from 'core/components/Elements/Command/Command.style';
import CommandLinkContent from 'core/components/Elements/Command/CommandLinkContent/CommandLinkContent';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import { formatCommandUrl } from 'core/utilities/command/command';
import type { EnhancedCommand } from 'core/utilities/core.types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

export interface ComponentProps {
  command: EnhancedCommand;
  small?: boolean;
}

function CommandFileDownload({ command, small }: ComponentProps) {
  const {
    id, title, active, shortcut, action, icon, label, styleHint, url,
  } = command;
  const [loading, setLoading] = useState(false);
  const { FileDownloadLoader, onStart } = useFileDownload(() => {
    setLoading(false);
  });

  const ref = React.createRef<HTMLButtonElement>();

  const shortcutTrigger = useShortcut(...(shortcut || []));

  useEffect(() => {
    if (shortcutTrigger && ref && ref.current) {
      ref.current.click();
    }
  }, [shortcutTrigger, ref]);

  const jsfUrl = useSelector((state: ApplicationState) => state.configuration.jsfUrl);
  const downloadFileUrl = formatCommandUrl(jsfUrl, command);

  return (
    <Box position="relative" display="inline-block">
      <ButtonLink
        action={action || 'tertiary'}
        url={downloadFileUrl || ''}
        externalLink
        className={classes.command}
        title={title}
        small={small}
        disabled={loading || active === false}
        e2eId={id}
        buttonRef={ref}
        buttonIcon={(icon && !label) || styleHint !== undefined}
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          // ctrl+click will open the link in a new tab, don't start the download here
          if (!e.ctrlKey) {
            e.preventDefault();
            setLoading(true);
            onStart(url as string, e);
          }
        }}
      >
        <CommandLinkContent command={command} />
      </ButtonLink>
      {loading && (
        <ButtonLoading />
      )}
      {FileDownloadLoader && <FileDownloadLoader />}
    </Box>
  );
}

export default CommandFileDownload;
